import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useParams } from 'react-router-dom';
import { Plus, ChevronDown, X } from 'lucide-react';
import FormPageLayout from '../components/common/FormPageLayout';
import Alert from '../components/common/Alert';
import DateTimeInput from '../components/common/DateTimeInput';
import CalculatorInput from '../components/common/CalculatorInput';
import ContactSelect from '../components/contacts/ContactSelect';
import { useCreateReceivable, useUpdateReceivable, useReceivableQuery } from '../hooks/useQueries';
import useModalBack from '../hooks/useModalBack';
import { useToast } from '../context/ToastContext';
import { formatDate } from '../utils/formatters';

const initialFormData = {
  contactId: '',
  contactName: '',
  description: '',
  amount: '',
  lentDate: formatDate(new Date(), 'input'),
  dueDate: '',
  reminderEnabled: true,
  reminderFrequency: 'weekly',
  reminderTime: '09:00',
  reminderDay: 1,
  reminderDayOfMonth: 1,
  reminderMonth: 1,
  notes: ''
};

const ALL_FREQUENCIES = [
  { value: 'daily', label: 'Daily', minDays: 0 },
  { value: 'weekly', label: 'Weekly', minDays: 7 },
  { value: 'monthly', label: 'Monthly', minDays: 30 },
  { value: 'quarterly', label: 'Quarterly', minDays: 90 },
  { value: 'yearly', label: 'Yearly', minDays: 365 }
];

const DAYS_OF_WEEK = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' }
];

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' }
];

const getDaysInMonth = (month) => {
  if ([1, 3, 5, 7, 8, 10, 12].includes(month)) return 31;
  if ([4, 6, 9, 11].includes(month)) return 30;
  return 29; // February (considering leap years)
};

export default function ReceivableFormPage() {
  const navigate = useNavigate();
  const { id } = useParams();

  const { toast } = useToast();
  const createMutation = useCreateReceivable();
  const updateMutation = useUpdateReceivable();

  // Fetch receivable if editing
  const { data: receivableData } = useReceivableQuery(id, { enabled: !!id });
  const receivable = receivableData?.receivable || receivableData;

  const [formData, setFormData] = useState(initialFormData);
  const [loading, setLoading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [error, setError] = useState(null);
  const [showNotes, setShowNotes] = useState(false);
  const [contactAutoOpen, setContactAutoOpen] = useState(false);
  const [pickerModal, setPickerModal] = useState(null); // 'day' | 'dayOfMonth' | 'month' | null
  useModalBack(!!pickerModal, () => setPickerModal(null), 'ReceivablePicker');

  const isEditing = !!id;

  // Calculate available frequencies based on date gap
  const availableFrequencies = useMemo(() => {
    if (!formData.lentDate || !formData.dueDate) {
      return ALL_FREQUENCIES; // Show all if no due date
    }
    const lent = new Date(formData.lentDate);
    const due = new Date(formData.dueDate);
    const diffDays = Math.ceil((due - lent) / (1000 * 60 * 60 * 24));
    return ALL_FREQUENCIES.filter(f => f.minDays <= diffDays);
  }, [formData.lentDate, formData.dueDate]);

  // Load receivable data when editing
  useEffect(() => {
    if (receivable && isEditing) {
      setFormData({
        contactId: receivable.contact?._id || '',
        contactName: receivable.contact?.name || '',
        description: receivable.description || '',
        amount: receivable.amount != null ? parseFloat(receivable.amount.toFixed(2)).toString() : '',
        lentDate: receivable.lentDate ? formatDate(receivable.lentDate, 'input') : formatDate(new Date(), 'input'),
        dueDate: receivable.dueDate ? formatDate(receivable.dueDate, 'input') : '',
        reminderEnabled: receivable.reminderEnabled !== false,
        reminderFrequency: receivable.reminderFrequency || 'weekly',
        reminderTime: receivable.reminderTime || '09:00',
        reminderDay: receivable.reminderDay ?? 1,
        reminderDayOfMonth: receivable.reminderDayOfMonth ?? 1,
        reminderMonth: receivable.reminderMonth ?? 1,
        notes: receivable.notes || ''
      });
      setShowNotes(!!receivable.notes);
    }
  }, [receivable, isEditing]);

  // Reset frequency if current selection is not available
  useEffect(() => {
    if (availableFrequencies.length > 0 && !availableFrequencies.find(f => f.value === formData.reminderFrequency)) {
      setFormData(prev => ({ ...prev, reminderFrequency: availableFrequencies[0].value }));
    }
  }, [availableFrequencies, formData.reminderFrequency]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleContactSelect = (contact) => {
    setFormData(prev => ({
      ...prev,
      contactId: contact._id,
      contactName: contact.name
    }));
    if (validationErrors.contactId) {
      setValidationErrors(prev => ({ ...prev, contactId: null }));
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.contactId) {
      errors.contactId = 'Please select a contact';
    }
    if (!formData.description?.trim()) {
      errors.description = 'Description is required';
    }
    if (!formData.amount || parseFloat(formData.amount) <= 0) {
      errors.amount = 'Please enter a valid amount';
    }
    if (!formData.lentDate) {
      errors.lentDate = 'Lent date is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) return;

    setLoading(true);
    setError(null);

    try {
      const payload = {
        contactId: formData.contactId,
        description: formData.description.trim(),
        amount: parseFloat(formData.amount),
        lentDate: formData.lentDate,
        dueDate: formData.dueDate || null,
        reminderEnabled: formData.reminderEnabled,
        reminderFrequency: formData.reminderFrequency || 'weekly',
        reminderTime: formData.reminderTime || '09:00',
        reminderDay: parseInt(formData.reminderDay) || 1,
        reminderDayOfMonth: parseInt(formData.reminderDayOfMonth) || 1,
        reminderMonth: parseInt(formData.reminderMonth) || 1,
        notes: formData.notes?.trim() || ''
      };

      if (isEditing) {
        await updateMutation.mutateAsync({ id, data: payload });
        toast.success('Receivable updated successfully');
      } else {
        await createMutation.mutateAsync(payload);
        toast.success('Receivable created successfully');
      }

      navigate('/receivables');
    } catch (err) {
      setError(err.error || 'Failed to save receivable');
    } finally {
      setLoading(false);
    }
  };

  // Avatar helpers
  const AVATAR_COLORS = [
    'bg-violet-500', 'bg-pink-500', 'bg-amber-500', 'bg-emerald-500',
    'bg-blue-500', 'bg-rose-500', 'bg-teal-500', 'bg-orange-500'
  ];
  const getInitials = (name) => {
    if (!name) return '?';
    const parts = name.trim().split(/\s+/);
    if (parts.length === 1) return parts[0][0].toUpperCase();
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  };
  const getAvatarColor = (name) => {
    if (!name) return AVATAR_COLORS[0];
    let hash = 0;
    for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash);
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
  };

  return (
    <FormPageLayout title={isEditing ? 'Edit Receivable' : 'Add Receivable'} backPath="/receivables">
      <form onSubmit={handleSubmit} className="space-y-5">
        {error && (
          <Alert type="error" message={error} onClose={() => setError(null)} />
        )}

        {/* Contact Selection */}
        <div>
          <label className="text-xs font-bold text-surface-900 uppercase tracking-wide mb-2 block">
            Who owes you? <span className="text-red-500">*</span>
          </label>
          {formData.contactId ? (
            <div className="flex items-center gap-3 p-3 bg-white rounded-xl border-2 border-primary-200">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-bold text-sm ${getAvatarColor(formData.contactName)}`}>
                {getInitials(formData.contactName)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-surface-900 text-sm truncate">{formData.contactName}</p>
              </div>
              <button
                type="button"
                onClick={() => { setFormData(prev => ({ ...prev, contactId: '', contactName: '' })); setContactAutoOpen(true); }}
                className="text-xs font-semibold px-3 py-1 rounded-lg"
                style={{ backgroundColor: 'var(--accent-light)', color: 'var(--accent-bg)' }}
              >
                Change
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              <div className="flex gap-2 items-stretch">
                <div className="flex-1">
                  <ContactSelect
                    value={formData.contactId}
                    displayValue={formData.contactName}
                    onChange={(contact) => { handleContactSelect(contact); setContactAutoOpen(false); }}
                    error={validationErrors.contactId}
                    onAddContact={() => navigate('/contacts/new?returnTo=' + encodeURIComponent(`/receivables/${isEditing ? id + '/edit' : 'new'}`))}
                    autoOpen={contactAutoOpen}
                  />
                </div>
                <button
                  type="button"
                  onClick={() => navigate('/contacts/new?returnTo=' + encodeURIComponent(`/receivables/${isEditing ? id + '/edit' : 'new'}`))}
                  className="flex items-center justify-center w-11 rounded-xl transition-colors"
                  style={{ backgroundColor: 'var(--accent-bg)', color: 'var(--accent-text)' }}
                  title="Add new contact"
                >
                  <Plus className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
          {validationErrors.contactId && (
            <p className="text-xs text-red-500 mt-1">{validationErrors.contactId}</p>
          )}
        </div>

        {/* Description */}
        <div>
          <label className="text-xs font-bold text-surface-900 uppercase tracking-wide mb-2 block">
            What for? <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="description"
            value={formData.description}
            onChange={handleChange}
            placeholder="e.g., Loan for car repair"
            className={`input ${validationErrors.description ? 'border-red-500' : ''}`}
          />
          {validationErrors.description && (
            <p className="text-xs text-red-500 mt-1">{validationErrors.description}</p>
          )}
        </div>

        {/* Amount */}
        <div>
          <label className="text-xs font-bold text-surface-900 uppercase tracking-wide mb-2 block">
            Amount <span className="text-red-500">*</span>
          </label>
          <CalculatorInput
            name="amount"
            value={formData.amount}
            onChange={(e) => {
              const val = e?.target?.value ?? e;
              setFormData(prev => ({ ...prev, amount: val }));
              if (validationErrors.amount) {
                setValidationErrors(prev => ({ ...prev, amount: null }));
              }
            }}
            placeholder="Enter amount"
            className="input"
            error={validationErrors.amount}
          />
        </div>

        {/* Dates - side by side */}
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-xs font-bold text-surface-900 uppercase tracking-wide mb-2 block">Lent Date <span className="text-red-500">*</span></label>
            <DateTimeInput
              dateOnly
              value={formData.lentDate}
              onChange={(date) => {
                setFormData(prev => ({ ...prev, lentDate: date }));
                if (validationErrors.lentDate) {
                  setValidationErrors(prev => ({ ...prev, lentDate: null }));
                }
              }}
              placeholder="Lent Date"
              error={validationErrors.lentDate}
            />
          </div>
          <div>
            <label className="text-xs font-bold text-surface-900 uppercase tracking-wide mb-2 block">Due Date</label>
            <DateTimeInput
              dateOnly
              value={formData.dueDate}
              onChange={(date) => setFormData(prev => ({ ...prev, dueDate: date }))}
              placeholder="Due Date"
            />
          </div>
        </div>

        {/* Reminder Settings - Card style */}
        <div className="bg-white rounded-xl border border-surface-200 overflow-hidden">
          <div className="flex items-center justify-between p-3">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-lg bg-amber-50 flex items-center justify-center">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#d97706" strokeWidth="2"><path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 01-3.46 0"/></svg>
              </div>
              <span className="text-sm font-semibold text-surface-900">Reminders</span>
            </div>
            <button
              type="button"
              onClick={() => setFormData(prev => ({ ...prev, reminderEnabled: !prev.reminderEnabled }))}
              className={`w-11 h-6 rounded-full transition-colors relative ${formData.reminderEnabled ? '' : 'bg-surface-200'}`}
              style={formData.reminderEnabled ? { backgroundColor: 'var(--accent-bg)' } : {}}
            >
              <div className={`w-5 h-5 rounded-full bg-white shadow-sm absolute top-0.5 transition-all ${formData.reminderEnabled ? 'left-[22px]' : 'left-0.5'}`} />
            </button>
          </div>

          {formData.reminderEnabled && (
            <div className="px-3 pb-3 pt-0 border-t border-surface-100">
              {/* Frequency Chips */}
              <label className="text-[11px] font-semibold text-surface-400 uppercase tracking-wide mt-3 mb-2 block">Frequency</label>
              <div className="flex flex-wrap gap-1.5">
                {availableFrequencies.map(freq => (
                  <button
                    key={freq.value}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, reminderFrequency: freq.value }))}
                    className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                      formData.reminderFrequency === freq.value
                        ? 'text-white'
                        : 'bg-surface-100 text-surface-600'
                    }`}
                    style={formData.reminderFrequency === freq.value ? { backgroundColor: 'var(--accent-bg)' } : {}}
                  >
                    {freq.label}
                  </button>
                ))}
              </div>

              {/* Day / Day of Month / Month / Time selectors */}
              <div className="grid grid-cols-2 gap-2.5 mt-3">
                {formData.reminderFrequency === 'weekly' && (
                  <div>
                    <label className="text-[11px] font-semibold text-surface-400 uppercase mb-1 block">Day</label>
                    <button
                      type="button"
                      onClick={() => setPickerModal('day')}
                      className="input text-sm py-2 w-full flex items-center justify-between"
                    >
                      <span>{DAYS_OF_WEEK.find(d => d.value === Number(formData.reminderDay))?.label || 'Select'}</span>
                      <ChevronDown className="w-4 h-4 text-surface-400" />
                    </button>
                  </div>
                )}

                {(formData.reminderFrequency === 'monthly' || formData.reminderFrequency === 'quarterly') && (
                  <div>
                    <label className="text-[11px] font-semibold text-surface-400 uppercase mb-1 block">Day of Month</label>
                    <button
                      type="button"
                      onClick={() => setPickerModal('dayOfMonth')}
                      className="input text-sm py-2 w-full flex items-center justify-between"
                    >
                      <span>{formData.reminderDayOfMonth}</span>
                      <ChevronDown className="w-4 h-4 text-surface-400" />
                    </button>
                  </div>
                )}

                {formData.reminderFrequency === 'yearly' && (
                  <>
                    <div>
                      <label className="text-[11px] font-semibold text-surface-400 uppercase mb-1 block">Month</label>
                      <button
                        type="button"
                        onClick={() => setPickerModal('month')}
                        className="input text-sm py-2 w-full flex items-center justify-between"
                      >
                        <span>{MONTHS.find(m => m.value === Number(formData.reminderMonth))?.label || 'Select'}</span>
                        <ChevronDown className="w-4 h-4 text-surface-400" />
                      </button>
                    </div>
                    <div>
                      <label className="text-[11px] font-semibold text-surface-400 uppercase mb-1 block">Day</label>
                      <button
                        type="button"
                        onClick={() => setPickerModal('dayOfMonth')}
                        className="input text-sm py-2 w-full flex items-center justify-between"
                      >
                        <span>{formData.reminderDayOfMonth}</span>
                        <ChevronDown className="w-4 h-4 text-surface-400" />
                      </button>
                    </div>
                  </>
                )}

                <div>
                  <label className="text-[11px] font-semibold text-surface-400 uppercase mb-1 block">Time</label>
                  <DateTimeInput
                    timeOnly
                    value={formData.reminderTime}
                    onChange={(time) => setFormData(prev => ({ ...prev, reminderTime: time }))}
                    placeholder="Time"
                  />
                </div>
              </div>

              {/* Reminder info note */}
              <div className="mt-3 flex gap-2 p-2.5 bg-blue-50 rounded-xl">
                <span className="text-sm flex-shrink-0">📧</span>
                <p className="text-[11px] text-blue-700 leading-relaxed">
                  We'll send email reminders to your contact based on the selected frequency. Please ensure the contact has an email address.
                  <span className="block mt-1 text-blue-500">WhatsApp & SMS reminders coming soon!</span>
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Notes - collapsible */}
        {!showNotes ? (
          <button
            type="button"
            onClick={() => setShowNotes(true)}
            className="flex items-center gap-1.5 text-sm font-semibold"
            style={{ color: 'var(--accent-bg)' }}
          >
            <Plus className="w-4 h-4" />
            Add Notes (Optional)
          </button>
        ) : (
          <div>
            <label className="text-xs font-bold text-surface-900 uppercase tracking-wide mb-2 block">Notes</label>
            <textarea
              name="notes"
              value={formData.notes}
              onChange={handleChange}
              rows={2}
              placeholder="Any additional notes..."
              className="input resize-none"
            />
          </div>
        )}

        {/* Submit Button - sticky on mobile */}
        <div className="sticky bottom-0 -mx-3 xl:mx-0 px-3 xl:px-0 py-3 bg-white xl:bg-transparent xl:static xl:pt-4 xl:border-t xl:border-surface-200">
          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 rounded-xl text-white font-bold text-[15px] transition-colors disabled:opacity-50"
            style={{ backgroundColor: 'var(--accent-bg)' }}
          >
            {loading ? 'Saving...' : isEditing ? 'Update Receivable' : 'Create Receivable'}
          </button>
        </div>
      </form>

      {/* Picker Bottom Sheet Modals */}
      {pickerModal && createPortal(
        <div
          className="fixed inset-0 bg-surface-900/60 backdrop-blur-sm flex items-end sm:items-center justify-center z-[100]"
          onClick={() => setPickerModal(null)}
        >
          <div
            className="bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl w-full sm:max-w-sm flex flex-col animate-slide-up sm:mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Drag handle */}
            <div className="flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 rounded-full bg-surface-300" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 border-b border-surface-100">
              <h3 className="text-lg font-semibold text-surface-900">
                {pickerModal === 'day' ? 'Select Day' : pickerModal === 'month' ? 'Select Month' : 'Select Day of Month'}
              </h3>
              <button
                type="button"
                onClick={() => setPickerModal(null)}
                className="p-1.5 text-surface-400 hover:text-surface-600 rounded-lg transition-colors hover:bg-surface-100"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Badge Grid */}
            <div className="px-4 py-4 pb-10">
              {pickerModal === 'day' && (
                <div className="flex flex-wrap justify-center gap-2">
                  {DAYS_OF_WEEK.map((day) => {
                    const isActive = Number(formData.reminderDay) === day.value;
                    return (
                      <button
                        key={day.value}
                        type="button"
                        onClick={() => { setFormData(prev => ({ ...prev, reminderDay: day.value })); setPickerModal(null); }}
                        className={`w-[calc(25%-8px)] py-2.5 rounded-xl text-sm font-semibold transition-all text-center ${
                          isActive ? 'text-white shadow-sm' : 'text-surface-700 bg-surface-50 hover:bg-surface-100'
                        }`}
                        style={isActive ? { backgroundColor: 'var(--accent-bg)' } : {}}
                      >
                        {day.label.slice(0, 3)}
                      </button>
                    );
                  })}
                </div>
              )}

              <div className={`grid gap-2 ${pickerModal === 'month' ? 'grid-cols-3' : 'grid-cols-7'}`} style={{ display: pickerModal === 'day' ? 'none' : undefined }}>
                {pickerModal === 'month' && MONTHS.map((month) => {
                  const isActive = Number(formData.reminderMonth) === month.value;
                  return (
                    <button
                      key={month.value}
                      type="button"
                      onClick={() => { setFormData(prev => ({ ...prev, reminderMonth: month.value })); setPickerModal(null); }}
                      className={`py-2.5 rounded-xl text-sm font-semibold transition-all ${
                        isActive ? 'text-white shadow-sm' : 'text-surface-700 bg-surface-50 hover:bg-surface-100'
                      }`}
                      style={isActive ? { backgroundColor: 'var(--accent-bg)' } : {}}
                    >
                      {month.label.slice(0, 3)}
                    </button>
                  );
                })}

                {pickerModal === 'dayOfMonth' && Array.from({ length: formData.reminderFrequency === 'yearly' ? getDaysInMonth(formData.reminderMonth) : 31 }, (_, i) => i + 1).map((day) => {
                  const isActive = Number(formData.reminderDayOfMonth) === day;
                  return (
                    <button
                      key={day}
                      type="button"
                      onClick={() => { setFormData(prev => ({ ...prev, reminderDayOfMonth: day })); setPickerModal(null); }}
                      className={`py-2 rounded-xl text-sm font-semibold transition-all ${
                        isActive ? 'text-white shadow-sm' : 'text-surface-700 bg-surface-50 hover:bg-surface-100'
                      }`}
                      style={isActive ? { backgroundColor: 'var(--accent-bg)' } : {}}
                    >
                      {day}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </FormPageLayout>
  );
}
